import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Flex, useBreakpointValue } from '@chakra-ui/react';
import { Info } from 'react-feather';
import { ProductGroup } from 'ts/client/gallery';
import {
    convertIEventPhotosToIImages,
    groupHasLabFulfilledCards,
    hasMultipleSubgroupsOfProducts,
    hasRenderableThumbnail
} from 'ts/client/gallery/components/common';
import { IImage } from 'ts/client/gallery/components/types';
import { Button } from 'ts/common/components/gallery';
import { EmptyState } from 'ts/common/components/gallery';
import { useTranslate } from 'ts/common/hooks';
import { getAssetMediaType, getProductMediaTypes } from 'ts/common/mediaType';
import { useStore } from 'client_react/gallery/hooks/StoreContext';
import { PageIndex } from '../StorePage';

interface LandingPageProps {
    navigate: (path: string, needsDigest?: boolean) => void;
}

const LandingPage: FC<LandingPageProps> = ({ navigate }) => {
    const {
        goToPage,
        photos,
        priceSheetGroups,
        priceSheet,
        setImageOnSelectedGroup,
        setSelectedPriceSheetItem,
        setSelectedPriceSheetGroup
    } = useStore();

    useEffect(() => {
        setSelectedPriceSheetItem(null);
    }, [setSelectedPriceSheetItem]);

    /**
     * One sample per group tile, drawn from the assets that group can actually sell. Clicking a
     * group whose only product is a print goes straight to its options page with this sample as
     * the preselected asset, so a video frame here would end up on the print mockup.
     *
     * A group none of whose products the gallery can complete gets no sample, and so no tile —
     * a gallery of videos alone has nothing to put on a print. Groups that sell photos only draw
     * from one shared batch of photos, exactly as they did before videos could be sold.
     */
    const imagesForGroups = useMemo(() => {
        // Only assets the conversion below will keep count as available, or a video still waiting
        // on its poster would let a mixed group through and then be dropped from its own sample.
        const availableMediaTypes = new Set(
            photos.filter(hasRenderableThumbnail).map((photo) => getAssetMediaType(photo))
        );
        const photoOnlyImages = convertIEventPhotosToIImages(
            photos.filter((photo) => getAssetMediaType(photo) === 'photo'),
            true,
            priceSheetGroups.length
        );

        return priceSheetGroups.map<Nullable<IImage>>((priceSheetGroup, index) => {
            const sellableMediaTypes = new Set(
                priceSheetGroup.items
                    .map((item) => getProductMediaTypes(item))
                    .filter((mediaTypes) =>
                        mediaTypes.every((mediaType) => availableMediaTypes.has(mediaType))
                    )
                    .flat()
            );

            if (sellableMediaTypes.size === 0) {
                return null;
            }

            if (sellableMediaTypes.size === 1 && sellableMediaTypes.has('photo')) {
                return photoOnlyImages[index] ?? null;
            }

            const [image] = convertIEventPhotosToIImages(
                photos.filter((photo) => sellableMediaTypes.has(getAssetMediaType(photo))),
                true,
                1
            );

            return image ?? null;
        });
    }, [photos, priceSheetGroups]);

    const hasAnyGroupImage = imagesForGroups.some((image) => image !== null);

    const isLargeScreen = useBreakpointValue({ base: false, lg: true });
    const gap = isLargeScreen ? 20 : 10;

    const productGroupMaxWidth = useBreakpointValue({
        base: `calc(50% - ${gap}px)`,
        lg: `calc(50% - ${gap / 2}px)`
    });

    const t = useTranslate('client.store.landingPage');

    const handleProductGroupSelection = useCallback(
        (priceSheetGroup: SpApi.Client.IPriceSheetGroup, selectedGroupImage: IImage) => {
            setSelectedPriceSheetGroup(priceSheetGroup);

            if (
                priceSheetGroup.groupType === 'cards' &&
                groupHasLabFulfilledCards(priceSheetGroup)
            ) {
                setImageOnSelectedGroup(selectedGroupImage);
                goToPage(PageIndex.SelectCard, t('cards'));
                return;
            }

            if (priceSheetGroup.items.length === 1) {
                setSelectedPriceSheetItem(priceSheetGroup.items[0]);
            }

            setImageOnSelectedGroup(selectedGroupImage);

            if (priceSheetGroup.groupType === 'packages') {
                goToPage(PageIndex.SelectPackage, t('packages'));
            } else if (hasMultipleSubgroupsOfProducts(priceSheetGroup)) {
                goToPage(PageIndex.SelectProduct, priceSheetGroup.name);
            } else {
                goToPage(PageIndex.SelectProductOptions, priceSheetGroup.name);
            }
        },
        [
            goToPage,
            setImageOnSelectedGroup,
            setSelectedPriceSheetItem,
            setSelectedPriceSheetGroup,
            t
        ]
    );

    useEffect(() => {
        const [onlyGroupImage] = imagesForGroups;

        if (onlyGroupImage && priceSheetGroups.length === 1) {
            handleProductGroupSelection(priceSheetGroups[0], onlyGroupImage);
        }
    }, [handleProductGroupSelection, imagesForGroups, priceSheetGroups]);

    return priceSheetGroups.length === 0 ? (
        <EmptyState page="Store" translateFilter={t} />
    ) : priceSheetGroups.length === 1 ? null : (
        <Flex
            data-testid="landing-page"
            flexDirection="column"
            height="100%"
            top={isLargeScreen ? '80px' : '64px'}
            width="100%"
            alignItems="center"
        >
            <Button
                borderRadius="24px"
                color="currentColor"
                data-testid="pricing-and-discounts-button"
                fontFamily="open-sans"
                leftIcon={<Info height="14px" style={{ marginRight: '8px' }} width="14px" />}
                marginBottom="30px"
                marginTop="30px"
                onClick={() => {
                    navigate('info/pricing');
                }}
                padding="12px 20px"
                text={`${t('pricingInfoLink')}${
                    priceSheet?.discountDescriptions.length ? ` ${t('andDiscounts')}` : ''
                }`}
                variant="outline primary"
            />
            <Flex
                flexWrap="wrap"
                gap={`${gap}px`}
                rowGap="20px"
                justifyContent="space-evenly"
                width="100%"
                maxWidth="1440px"
            >
                {hasAnyGroupImage &&
                    priceSheetGroups.map((priceSheetGroup, index) => {
                        const image = imagesForGroups[index];

                        return image ? (
                            <ProductGroup
                                forceDefaultBackdrop
                                data-testid="product-group"
                                priceSheetGroup={priceSheetGroup}
                                image={image}
                                key={index}
                                maxWidth={productGroupMaxWidth}
                                width="388px"
                                onClick={() => {
                                    handleProductGroupSelection(priceSheetGroup, image);
                                }}
                            />
                        ) : null;
                    })}
            </Flex>
        </Flex>
    );
};

export default LandingPage;
